<template>
  <div class="chart-wrapper">
    <div class="header">
      <div class="filter-container">
        <p class="title">{{ title }}</p>
        <div class="d-flex ga-3">
                  <v-menu location="bottom">
          <template v-slot:activator="{ props }">
            <v-chip
              v-bind="props"
              class="filter-chip"
              variant="outlined"
              append-icon="mdi-chevron-down"
            >
              {{ filterName }}
            </v-chip>
          </template>
          <v-list density="compact" class="filter-options-list">
            <div v-for="option in filterOptions" :key="option.value">
              <v-list-item
                :value="option.value"
                @click="selectFilter(option.value)"
                :class="{ 'selected-item': option.value === filter, 'mb-2': option.value == 'period' }"
              >
                <v-list-item-title>{{ option.name }}</v-list-item-title>
              </v-list-item>
              <v-divider v-if="option.value == 'period'"></v-divider>
            </div>
          </v-list>
        </v-menu>
          <rb-date-picker v-if="currentFilter == 'period'"
            v-model="startDate"
            label="Data de início"
            :max="endDate"
          />
          <rb-date-picker v-if="currentFilter == 'period'"
            v-model="endDate"
            label="Data de término"
            :min="startDate"
          />
        </div>
      </div>
    </div>
    <div ref="chartEl" class="chart"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, defineEmits, defineProps, computed } from 'vue';
import * as echarts from 'echarts';
import { useFinanceStore } from '@/assistents/stores/finance';
import RbDatePicker from '@/ai_studio/components/common/RbDatePicker.vue';

const props = defineProps({
  data: { type: Array, default: () => [] },
  yAxisConfig: { type: Object, default: () => ({ min: 0, max: 100, interval: 10 }) },
  title: { type: String, default: 'Gráfico' },
  gradient: { type: Array, default: () => ['#eea88a', '#e34400'] },
  loading: { type: Boolean, default: false },
  currentFilter: { type: String, default: 'this_week' },
});

const emit = defineEmits(['filter-change']);

const store = useFinanceStore();
const chartEl = ref(null);
let chart = null;
const startDate = ref(null);
const endDate = ref(null);

const filter = computed(() => props.currentFilter);

const filterOptions = [
  { name: 'Selecionar período', value: 'period' },
  { name: 'Hoje', value: 'today' },
  { name: 'Semana atual', value: 'this_week' },
  { name: 'Mês atual', value: 'this_month' },
  { name: 'Mês passado', value: 'last_month' },
  { name: 'Este ano', value: 'this_year' },
  { name: 'Ano passado', value: 'last_year' },
];

const axisConfigs = {
  today: { zoom: 8 },
  this_week: { zoom: 4 },
  this_month: { zoom: 8 },
  last_month: { zoom: 8 },
  this_year: { zoom: 6 },
  last_year: { zoom: 6 },
  period: { zoom: 8 },
};

const formatters = {
  today: (val) => new Date(val).getHours().toString().padStart(2, '0') + ':00',
  this_year: (val) => {
    const date = new Date(val + '-02');
    const month = date.toLocaleString('pt-BR', { month: 'long' });
    return month.charAt(0).toUpperCase() + month.slice(1);
  },
  last_year: (val) => formatters.this_year(val),
  this_week: (val) => {
    const date = new Date(val.replace(/-/g, '/'));
    const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    return days[date.getDay()];
  },
  this_month: (val) => {
    const date = new Date(val.replace(/-/g, '/'));
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  },
  last_month: (val) => formatters.this_month(val),
  period: (val) => isSingleDay.value ? formatters.today(val) : formatters.this_month(val)
};

const isSingleDay = computed(() => {
  if (filter.value !== 'period' || !startDate.value || !endDate.value) return false;
  const start = new Date(startDate.value).toISOString().slice(0, 10);
  const end = new Date(endDate.value).toISOString().slice(0, 10);
  return start === end;
});

const filterName = computed(() => {
  const prefix = 'Período: ';
  if (filter.value === 'period') return prefix + 'Selecionar período';
  return prefix + filterOptions.find(opt => opt.value === filter.value)?.name;
});

const selectFilter = (val) => {
  emit('filter-change', { filter: val });
  if (val !== 'period') {
    startDate.value = null;
    endDate.value = null;
  }
};

const formatX = (val) => formatters[filter.value]?.(val) || val;

const formatY = (val) => store.plan === 'free' ? `${val}%` : val.toLocaleString('pt-BR');

const formatTip = (params) => {
  if (!params?.length) return '';

  const [dateStr, usage] = params[0].data;

  const formatDate = (str) => {
    if (filter.value === 'today') {
      return new Date(str).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    }

    if (filter.value === 'this_year' || filter.value === 'last_year') {
      const date = new Date(str + '-02');
      const formatted = date.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }

    if (filter.value === 'period') {
      return isSingleDay.value
        ? new Date(str).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : new Date(str.replace(/-/g, '/')).toLocaleDateString('pt-BR');
    }

    return new Date(str.replace(/-/g, '/')).toLocaleDateString('pt-BR');
  };

  const usageDisplay = store.plan === 'free' ? `${usage}%` : `${usage.toLocaleString('pt-BR')} tokens`;
  return `${formatDate(dateStr)}<br/>Uso: ${usageDisplay}`;
};

const createOption = () => ({
  tooltip: { trigger: 'axis', formatter: formatTip },
  grid: { left: '0%', right: '0%', bottom: '20%', top: '0%' },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: props.data.map(item => item[0]),
    axisLine: { show: false },
    axisLabel: {
      color: '#505353',
      fontSize: 12,
      interval: 0,
      rotate: 0,
      formatter: formatX,
      margin: 16,
    },
  },
  yAxis: {
    type: 'value',
    min: props.yAxisConfig.min,
    max: props.yAxisConfig.max,
    interval: props.yAxisConfig.interval,
    axisLabel: { formatter: formatY, color: '#232626', fontSize: 12, margin: 12 },
    splitLine: {
      lineStyle: {
        width: 1.25,
        color: ['#232626', '#C4C7C6', '#C4C7C6', '#C4C7C6', '#C4C7C6', '#C4C7C6', '#C4C7C6', '#C4C7C6', '#C4C7C6', '#C4C7C6', '#C4C7C6']
      }
    },
  },
  dataZoom: [
    {
      type: 'inside',
      startValue: 0,
      endValue: Math.max(0, (axisConfigs[filter.value]?.zoom || 4) - 1),
      zoomLock: true,
      zoomOnMouseWheel: false
    },
    {
      type: 'slider',
      height: 4,
      bottom: 5,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      fillerColor: '#8E9191',
      showDetail: false,
      handleSize: '0%',
      brushSelect: false,
      zoomLock: true
    },
  ],
  series: [{
    type: 'line',
    data: props.data,
    smooth: 0.4,
    showSymbol: false,
    lineStyle: {
      width: 2.5,
      color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [
        { offset: 0, color: props.gradient[0] },
        { offset: 1, color: props.gradient[1] }
      ]),
    },
  }],
});

const updateChart = () => chart?.setOption(createOption(), true);

watch([startDate, endDate], ([start, end]) => {
  if (filter.value !== 'period' || !start || !end) return;
  const startStr = new Date(start).toISOString().slice(0, 10);
  const endStr = new Date(end).toISOString().slice(0, 10);
  emit('filter-change', { filter: 'period', startDate: startStr, endDate: endStr });
});

watch(() => store.plan, updateChart);
watch(() => props.data, updateChart, { deep: true });
watch(() => props.loading, (loading) => {
  if (!chart) return;
  loading ? chart.showLoading(null, {
    text: '',
    color: '#00A1A1',
    maskColor: 'rgba(255, 255, 255, 0.8)',
    lineWidth: 4
  }) : chart.hideLoading();
});

onMounted(() => {
  if (!chartEl.value) return;
  chart = echarts.init(chartEl.value);
  updateChart();
  emit('filter-change', { filter: filter.value });
  setTimeout(() => chart?.resize(), 100);
  window.addEventListener('resize', () => chart?.resize());
});

onUnmounted(() => {
  chart?.dispose();
  window.removeEventListener('resize', () => chart?.resize());
});
</script>

<style scoped lang="scss">
.chart-wrapper {
  text-align: left;
  max-width: 100%;
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  box-shadow:
    0px 1px 3px 1px #00141426,
    0px 1px 2px 0px #006a6a4d;
}

.header {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 16px;
}

.filter-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.date-pickers-container {
  display: flex;
  gap: 8px;
  padding-bottom: 2px;
}

.title {
  margin: 0;
  font-weight: 500;
  font-size: 16px;
  color: #505353;
}

.filter-chip {
  height: 32px;
  min-width: 160px;
  border-radius: 8px;
  border-color: #c4c7c6;
  font-weight: 500;
  font-size: 14px;
  color: #505353;
  background-color: #f8faf9;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #f2f7f7;
  }

  :deep(.v-chip__content) {
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
  }
}

.filter-options-list {
  :deep(.v-list-item.selected-item) {
    background-color: #f2f7f7;
  }

  :deep(.v-list-item:hover) {
    background-color: #f2f7f7;
  }
}

.chart {
  width: 100%;
  flex: 1;
  min-height: 280px;
}
</style>
